import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { database } from "../firebase";
import AltEntriesHeader from "../components/alt-entries/AltEntriesHeader";
import AltEntriesFilterCard from "../components/alt-entries/AltEntriesFilterCard";
import AltEntriesKpiGrid from "../components/alt-entries/AltEntriesKpiGrid";
import AltEntriesCardList from "../components/alt-entries/AltEntriesCardList";
import AltExportModal from "../components/alt-entries/AltExportModal";

const ClearedEntries = () => {
  const [allRawEntries, setAllRawEntries] = useState([]);
  const [entries, setEntries] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [viewBy, setViewBy] = useState("all"); // "all", "shop", "customer", "deductions"
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(false);

      const [entriesSnap, deductionsSnap] = await Promise.all([
        database.collection("alt_entries").orderBy("created", "desc").get(),
        database
          .collection("alt_deductions")
          .orderBy("createdAt", "desc")
          .get(),
      ]);

      const allEntryDocs = entriesSnap.docs.map((d) => ({
        _id: d.id,
        recordType: "entry",
        ...d.data(),
      }));

      // Only cleared entries for page view
      const clearedEntryDocs = allEntryDocs.filter((doc) => !!doc.isCleared);

      const deductionDocs = deductionsSnap.docs.map((d) => ({
        _id: d.id,
        recordType: "deduction",
        ...d.data(),
      }));

      setAllRawEntries(allEntryDocs);
      setEntries(clearedEntryDocs);
      setDeductions(deductionDocs);

      // Default: checked for cleared items so user can view totals
      const initial = new Set();
      clearedEntryDocs.forEach((doc) => initial.add(doc._id));
      deductionDocs.forEach((doc) => initial.add(doc._id));
      setCheckedIds(initial);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const combinedList = useMemo(() => {
    const combined = [...entries, ...deductions];
    combined.sort((a, b) => {
      const timeA = new Date(a.created || a.createdAt || 0).getTime();
      const timeB = new Date(b.created || b.createdAt || 0).getTime();
      return timeB - timeA;
    });
    return combined;
  }, [entries, deductions]);

  const filteredItems = useMemo(() => {
    let list = combinedList;

    if (viewBy === "entries") {
      list = list.filter((doc) => doc.recordType === "entry");
    } else if (viewBy === "shop") {
      list = list.filter((doc) => doc.recordType === "entry" && doc.category === "shop");
    } else if (viewBy === "customer") {
      list = list.filter((doc) => doc.recordType === "entry" && doc.category === "customer");
    } else if (viewBy === "deductions") {
      list = list.filter((doc) => doc.recordType === "deduction");
    }

    if (search.trim() !== "") {
      const regex = new RegExp(search.trim(), "i");
      list = list.filter(
        (doc) =>
          regex.test(doc.phone || "") ||
          regex.test(doc.customerPhone || "") ||
          regex.test(doc.customer || "") ||
          regex.test(doc.issue || "") ||
          regex.test(doc.category || "") ||
          regex.test(doc.note || "") ||
          regex.test(doc.clearBatchId || ""),
      );
    }

    return list;
  }, [combinedList, search, viewBy]);

  // KPI Stats computed from checked cleared items
  const kpiStats = useMemo(() => {
    let shopTotal = 0;
    let sunilC = 0;
    let customerTotal = 0;

    filteredItems.forEach((doc) => {
      if (!checkedIds.has(doc._id)) return;

      const isDeduction =
        doc.type === "deduction" || doc.recordType === "deduction";

      if (isDeduction) {
        shopTotal -= Number(doc.amount || 0);
        return;
      }

      const q = Number(doc.qty || 1);
      if (doc.category === "shop") {
        shopTotal += Number(doc.amount || 0) * q;
      } else if (doc.category === "customer") {
        sunilC += Number(doc.amount || 0) * q;
      }
      customerTotal += Number(doc.customerAmount || 0) * q;
    });

    return {
      shopTotal,
      sunilC,
      customerTotal,
      profit: customerTotal - sunilC,
    };
  }, [filteredItems, checkedIds]);

  const handleToggle = useCallback((id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(
    (checked) => {
      setCheckedIds((prev) => {
        const next = new Set(prev);
        filteredItems.forEach((doc) => {
          if (checked) {
            next.add(doc._id);
          } else {
            next.delete(doc._id);
          }
        });
        return next;
      });
    },
    [filteredItems],
  );

  const allCheckedState = useMemo(() => {
    if (filteredItems.length === 0) return false;
    const allChecked = filteredItems.every((doc) => checkedIds.has(doc._id));
    const noneChecked = filteredItems.every((doc) => !checkedIds.has(doc._id));
    if (allChecked) return true;
    if (noneChecked) return false;
    return "indeterminate";
  }, [filteredItems, checkedIds]);

  return (
    <motion.div style={{ paddingBottom: "40px" }}>
      {/* Header */}
      <AltEntriesHeader
        title="Cleared Entries"
        onOpenExport={() => setExportModalVisible(true)}
      />

      {/* Top Filter Controls Card */}
      <AltEntriesFilterCard
        search={search}
        onSearchChange={setSearch}
        viewBy={viewBy}
        onViewByChange={setViewBy}
        allChecked={allCheckedState}
        onToggleAll={handleToggleAll}
      />

      {/* 4 KPI Summary Grid Cards */}
      <AltEntriesKpiGrid kpiStats={kpiStats} />

      {/* Card List (No floating bar) */}
      <AltEntriesCardList
        loading={loading}
        error={error}
        filteredItems={filteredItems}
        viewBy={viewBy}
        checkedIds={checkedIds}
        onToggle={handleToggle}
      />

      {/* Flexible Export Modal — Passed ALL raw entries (both uncleared & cleared) + deductions */}
      <AltExportModal
        visible={exportModalVisible}
        onClose={() => setExportModalVisible(false)}
        allData={[...allRawEntries, ...deductions]}
      />
    </motion.div>
  );
};

export default ClearedEntries;
